import riskAppetiteData from '../data/riskAppetite.json'
import businessScopingData from '../data/businessScoping.json'
import maturityAssessmentData from '../data/maturityAssessment.json'

// Build lookup maps for question labels
const questionLabelMap = {}

// Risk appetite
riskAppetiteData.questions?.forEach(q => {
  questionLabelMap[q.id] = q.question || q.domain || q.id
})

// Business scoping (nested sections)
businessScopingData.sections?.forEach(section => {
  ;(section.questions || []).forEach(q => {
    questionLabelMap[q.id] = q.question || q.label || q.id
  })
})

// Maturity assessment
maturityAssessmentData.sections?.forEach(section => {
  ;(section.questions || []).forEach(q => {
    questionLabelMap[q.id] = q.question || q.id
  })
})

const SECTION_LABELS = {
  riskAppetite: 'Risk Appetite',
  businessScoping: 'Business Scoping',
  technologyScoping: 'Technology Scoping',
  maturityAssessment: 'Maturity Assessment'
}

// DB column names mapped to response keys
const SECTION_DB_MAP = {
  riskAppetite: 'risk_appetite',
  businessScoping: 'business_scoping',
  technologyScoping: 'technology_scoping',
  maturityAssessment: 'maturity_assessment'
}

/**
 * Compare two response objects and return an array of changes.
 * Skips metadata keys like notes, impactData, threatContext.
 */
export function computeAssessmentDiff(originalResponses, newResponses) {
  const changes = []
  const sections = ['riskAppetite', 'businessScoping', 'technologyScoping', 'maturityAssessment']
  const skipKeys = new Set(['notes', 'impactData', 'threatContext', 'bs-0', 'business_model',
    'operational_hq', 'security_team_size', 'data_residency_states', 'office_location_states',
    'international_locations', 'security_function_model'])

  for (const section of sections) {
    const original = originalResponses[section] || {}
    const updated = newResponses[section] || {}
    const allKeys = new Set([...Object.keys(original), ...Object.keys(updated)])

    for (const key of allKeys) {
      if (skipKeys.has(key)) continue

      const oldVal = original[key]
      const newVal = updated[key]

      // Compare by JSON string to handle objects/arrays
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({
          section,
          sectionLabel: SECTION_LABELS[section],
          sectionDbKey: SECTION_DB_MAP[section],
          questionId: key,
          questionLabel: questionLabelMap[key] || key,
          previousValue: oldVal,
          newValue: newVal
        })
      }
    }
  }

  return changes
}

/**
 * Format a response value for human-readable display.
 */
export function formatResponseValue(section, value) {
  if (value === null || value === undefined) return 'unanswered'

  if (section === 'maturityAssessment') {
    const labels = { yes: 'Yes', mostly: 'Mostly', partially: 'Partially', no: 'No' }
    return labels[value] || String(value)
  }

  if (section === 'riskAppetite') {
    return `${value} / 5`
  }

  // Business/tech scoping - handle { value, weight } objects
  if (typeof value === 'object' && value?.value !== undefined) {
    const v = value.value
    if (Array.isArray(v)) return v.join(', ')
    return String(v)
  }

  if (Array.isArray(value)) return value.join(', ')

  return String(value)
}

export { SECTION_LABELS, questionLabelMap }
